import { Navigate } from 'react-router';
import { Plus } from 'lucide-react';
import { ROLE_LABELS, STAGE_LABELS } from '@seventide/types';
import { useMe } from '../../hooks/useMe';
import { readStorage, STORAGE_KEYS } from '../../lib/storage';
import { ButtonLink, LinkCard, PageHeader, PageLoader } from '../../components/ui';
import { PlainLayout } from '../layout/PlainLayout';

/**
 * `/` decides where a signed-in person goes: no workspaces → onboarding; one → that workspace;
 * several → the last one used on this device, otherwise a chooser.
 */
export function RootRedirect() {
  const me = useMe();
  if (!me.data) return <PageLoader />;
  const workspaces = me.data.workspaces;
  if (workspaces.length === 0) return <Navigate to="/onboarding" replace />;
  if (workspaces.length === 1 && workspaces[0]) return <Navigate to={`/w/${workspaces[0].id}`} replace />;
  const last = readStorage(STORAGE_KEYS.lastWorkspace);
  if (last && workspaces.some((w) => w.id === last)) return <Navigate to={`/w/${last}`} replace />;
  return <WorkspaceChooser />;
}

export function WorkspaceChooser() {
  const me = useMe();
  const workspaces = me.data?.workspaces ?? [];
  return (
    <PlainLayout>
      <PageHeader title="Choose a family workspace" description="You are a member of more than one family workspace." />
      <ul className="flex flex-col gap-3">
        {workspaces.map((w) => (
          <li key={w.id}>
            <LinkCard
              to={`/w/${w.id}`}
              title={w.principalName ?? w.name}
              description={`${w.name} · ${STAGE_LABELS[w.stage]} stage · You are: ${ROLE_LABELS[w.role]}`}
            />
          </li>
        ))}
      </ul>
      <ButtonLink to="/onboarding" variant="secondary" className="mt-6" leftIcon={<Plus aria-hidden="true" className="h-5 w-5" />}>
        Set up another family workspace
      </ButtonLink>
    </PlainLayout>
  );
}
